import Button from '@components/common/button/Button';
import GalleryImageSkeleton from '@components/common/gallery/GalleryImageSkeleton';
import ImageLightbox from '@components/common/lightbox/ImageLightbox';
import { FullScreenSpinner, isArrayEmpty, SelectInput } from '@hclinear/hc-linear-react';
import { t } from '@localization/i18n';
import { Clear } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { ImageGalleryLightboxState } from '@type/imageGallery/ImageGalleryLightboxState';
import React, { ReactNode, useState } from 'react';
import {
  GalleryImage,
  galleryOrderSelectField,
  imageGalleryLightBoxDefaultValue,
} from '@/components';
import { Gallery } from '@/lib/imageGallery';
import { ImageDTO } from '@/model';
import { GalleryFilterSortByType } from '@/type';

interface ImageGalleryProps {
  items: Item[];
  selectedId: number;
  onSelect: (imageId: number) => void;
  filter: string;
  updateFilter: (filterText: string) => void;
  onSubmit: (id?: number) => void;
  sortDirection?: 'asc' | 'desc';
  sortBy?: GalleryFilterSortByType;
  updateSort?: (field: GalleryFilterSortByType, dir: 'asc' | 'desc') => void;
  isLoading?: boolean;
  postSearchAction?: ReactNode;
  preSelectCustomAction?: ReactNode;
  postSelectCustomAction?: ReactNode;
  fullSizeImageQueryOptions?: (
    id: number | undefined,
  ) => UseQueryOptions<unknown, any, string | undefined, any>;
}

export const ImageGallery = ({
  items,
  selectedId,
  onSelect,
  filter,
  updateFilter,
  onSubmit,
  sortDirection,
  sortBy,
  updateSort,
  isLoading = false,
  postSearchAction,
  preSelectCustomAction,
  postSelectCustomAction,
  fullSizeImageQueryOptions,
}: ImageGalleryProps) => {
  const [lightboxState, setLightboxState] = useState<ImageGalleryLightboxState>(
    imageGalleryLightBoxDefaultValue,
  );

  const {
    data: fetchedFullSizeImage,
    isFetched: isFullSizeImageFetched,
    isLoading: isFullSizeImageLoading,
  } = useQuery({
    enabled: false,
    ...fullSizeImageQueryOptions?.(lightboxState.imageId),
  });

  const skeletonCount = 24;
  const hasFullSizeImageQuery = !!fullSizeImageQueryOptions;
  const { isOpen: isLightboxStateOpen } = lightboxState;
  const shouldRenderLightbox = hasFullSizeImageQuery
    ? isFullSizeImageFetched && isLightboxStateOpen
    : isLightboxStateOpen;

  const onLightboxOpen = (index: number) =>
    setLightboxState({
      isOpen: true,
      currentIndex: hasFullSizeImageQuery ? 0 : index,
      imageSources: hasFullSizeImageQuery ? [] : items.map((item) => item.file),
      imageId: hasFullSizeImageQuery ? items?.[index]?.id : undefined,
    });

  const onImageLightBoxClose = () => setLightboxState(imageGalleryLightBoxDefaultValue);

  const onImageSelect = (_: number, image: ImageDTO) => {
    onSelect(image.id);
  };

  const onImageDoubleClick = (item: Item) => onSubmit?.(item?.id);

  const onSortingChange = (fieldVale: `${GalleryFilterSortByType}_${'asc' | 'desc'}`) => {
    const [field, direction] = fieldVale.split('_');
    updateSort(field as GalleryFilterSortByType, direction as 'asc' | 'desc');
  };

  const renderOrderSelect = () => {
    if (!updateSort) {
      return;
    }
    return (
      <SelectInput
        selectField={galleryOrderSelectField}
        // @ts-ignore
        field={{
          value: `${sortBy}_${sortDirection}`,
          onChange: onSortingChange,
        }}
      />
    );
  };

  const getTooltipText = (item: Item) => {
    const { width, height, uploadDate, name } = item;
    if (!width || !height || !uploadDate) {
      return name;
    }

    return `${item.width}x${item.height} ${item.uploadDate}`;
  };

  const skeletonImageItems = Array.from({ length: skeletonCount }).map((_, index) => ({
    id: `skeleton-${index}`,
    src: '',
    width: 500,
    height: 500,
  }));

  const imageItems = () => {
    if (isLoading) return skeletonImageItems;

    return items?.map((item, index) => ({
      id: item.id,
      src: `data:image/png;base64,${item.file}`,
      width: 500,
      height: 500,
      isSelected: Number(selectedId) === Number(item.id),
      caption: item.name,
      tooltipText: getTooltipText(item),
    }));
  };

  return (
    <>
      <Box
        display='flex'
        justifyContent={postSelectCustomAction ? 'space-between' : 'flex-start'}
        alignItems='center'
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '16px',
          borderBottom: '1px solid #ddd',
          gap: 2,
        }}
      >
        <Box display='flex' alignItems='center' gap={2} sx={{ flex: 1 }}>
          <TextField
            label={t('common.search')}
            variant='outlined'
            value={filter}
            onChange={(e) => updateFilter(e.target.value)}
            fullWidth={!postSelectCustomAction}
            sx={{ width: postSelectCustomAction ? '300px' : undefined }}
            InputProps={{
              endAdornment: filter ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={() => updateFilter('')}>
                    <Clear fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {postSearchAction && <Box>{postSearchAction}</Box>}

          {updateSort && (
            <Box sx={{ width: postSelectCustomAction ? '250px' : '400px' }}>
              {renderOrderSelect()}
            </Box>
          )}

          {preSelectCustomAction && <Box>{preSelectCustomAction}</Box>}

          <Button
            variant='contained'
            disabled={!selectedId}
            color='primary'
            onClick={() => onSubmit(null)}
          >
            {t('common.choose')}
          </Button>
        </Box>

        {postSelectCustomAction && <Box>{postSelectCustomAction}</Box>}
      </Box>

      <div
        style={{
          width: '100%',
          margin: '0 auto',
          backgroundColor: 'rgba(234,234,234,0.1)',
          minHeight: '70vh',
          overflowX: 'hidden',
        }}
      >
        {!isLoading && isArrayEmpty(items) && (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            height='100%'
            sx={{
              backgroundColor: 'rgba(234,234,234,0.1)',
              padding: 5,
            }}
          >
            <Typography variant='h5'>{t('image.gallery-no-available-image')}</Typography>
          </Box>
        )}

        <Gallery<any>
          onClick={isLoading ? undefined : onImageSelect}
          images={imageItems()}
          margin={15}
          thumbnailImageComponent={(props) =>
            isLoading ? (
              <GalleryImageSkeleton style={props.imageProps.style} />
            ) : (
              <GalleryImage
                {...props}
                onOpenLightbox={() => onLightboxOpen(props.index)}
                onDoubleClick={() => onImageDoubleClick(items[props.index])}
              />
            )
          }
        />

        {isFullSizeImageLoading && <FullScreenSpinner openProp={true} />}

        {shouldRenderLightbox && (
          <ImageLightbox
            isOpen={lightboxState.isOpen}
            onClose={onImageLightBoxClose}
            imageIndex={lightboxState.currentIndex}
            imageSources={
              hasFullSizeImageQuery ? [fetchedFullSizeImage!] : lightboxState.imageSources
            }
          />
        )}
      </div>
    </>
  );
};

type Item = {
  id: number;
  name: string;
  file: string;
  width?: number;
  height?: number;
  uploadDate?: string;
};
